import { useState } from "react";
import useLiveApi from "./use-live-api";
import { VehicleAccess, VehicleAccessUpdateBody } from "../interfaces";

interface VehicleAccessApi {
  loading: boolean;
  error: boolean;
  updating: boolean;
  updateSuccess: boolean;
  getVehicleAccess: (id: string) => Promise<VehicleAccess | undefined>;
  updateVehicleAccess: (data: VehicleAccessUpdateBody) => Promise<void>;
  createVehicleAccess: (data: VehicleAccessUpdateBody) => Promise<void>;
  deleteVehicleAccess: (id: string) => Promise<void>;
  deleting: boolean;
}

export const useVehicleAccessApi = (): VehicleAccessApi => {
  const { apiGatewayFetch } = useLiveApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<boolean>(false);
  const [updating, setUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const getVehicleAccess = async (id: string) => {
    try {
      setLoading(true);
      const response = await apiGatewayFetch(`/vehicle-access/${id}`, "get");
      if (response.status !== 200) {
        throw new Error(response.status.toString());
      }
      const result = await response.json();
      const vehicleAccess = result as VehicleAccess;
      setLoading(false);

      return vehicleAccess;
    } catch (e) {
      console.error("error fetching vehicle access", e);
      setError(true);
      setLoading(false);

      return undefined;
    }
  };

  const updateVehicleAccess = async (data: VehicleAccessUpdateBody) => {
    try {
      setLoading(true);
      setUpdating(true);
      const response = await apiGatewayFetch(
        `/vehicle-access/${data.id}`,
        "put",
        JSON.stringify(data)
      );
      if (response.status !== 200) {
        throw new Error(response.status.toString());
      }
      setUpdateSuccess(true);
      await getVehicleAccess(data.id);
    } catch (e) {
      console.error("error updating vehicle", e);
      setError(true);
    } finally {
      setLoading(false);
      setUpdating(false);
    }
  };

  const deleteVehicleAccess = async (id: string) => {
    try {
      setDeleting(true);
      const response = await apiGatewayFetch(`/vehicle-access/${id}`, "delete");
      if (response.status !== 204) {
        throw new Error(response.status.toString());
      }
    } catch (e) {
      console.error("error deleting vehicle access", e);
      setError(true);
    } finally {
      setDeleting(false);
    }
  };

  const createVehicleAccess = async (data: VehicleAccessUpdateBody) => {
    try {
      setLoading(true);
      const response = await apiGatewayFetch(
        "/vehicle-access",
        "post",
        JSON.stringify(data)
      );
      if (response.status !== 201) {
        throw new Error(response.status.toString());
      }
    } catch (e) {
      console.error("error creating vehicle", e);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    updating,
    updateSuccess,
    getVehicleAccess,
    updateVehicleAccess,
    createVehicleAccess,
    deleteVehicleAccess,
    deleting,
  };
};
